import React, { useMemo } from 'react';
import UpdateChildrenArticlePriceProperties from './UpdateChildrenArticlePriceProperties';
import { useStyles } from './UpdateChildrenArticlePrice.style';
import { Observer } from 'mobx-react-lite';
import clsx from 'clsx';
import UpdateChildrenArticlePriceStore from './UpdateChildrenArticlePriceStore';
import { useCommonContext } from '../../../../contexts/CommonContext';
import BoxCard from '../../../../ui_components/BoxCard/BoxCard';
import PageLoading from '../../../../ui_components/PageLoading/PageLoading';
import Checkbox from '../../../../ui_components/Checkbox/Checkbox';
import Button from '../../../../ui_components/Button/Button';
import ButtonType from '../../../../ui_components/Button/ButtonType';
import { useI18nContext } from '../../../../contexts/I18nContext';

const UpdateChildrenArticlePrice: React.FC<UpdateChildrenArticlePriceProperties> = ({
    affectedPrices,
    newValueDifference,
    articleId,
    salePriceId,
    deliveryTimeDays,
    onSave,
    onCancel,
}) => {
    const classes = useStyles();
    const commonContext = useCommonContext();
    const i18n = useI18nContext();

    const store = useMemo(
        () =>
            new UpdateChildrenArticlePriceStore(
                commonContext,
                articleId || '',
                salePriceId || '',
                deliveryTimeDays,
                affectedPrices,
                newValueDifference,
            ),
        [commonContext, articleId, salePriceId, deliveryTimeDays, affectedPrices, newValueDifference]
    );

    const handleCheckboxChange = (agencyId: string) => {
        store.togglePriceSelection(agencyId);
    };

    const formatDifference = (value?: number) => {
        if (value == null) return '';
        const sign = value >= 0 ? '+' : '';
        return `${sign}${value.toFixed(2)}`;
    };

    const onSalePriceValuesSave = async () => {
        onSave && onSave(store.affectedPrices.filter((price) => price.isSelected)
            .map((price) => ({
                articlePriceId: price.articlePriceId,
                price: price.newPrice,
            })));
    };

    const onHandleCancel = async () => {
        onCancel && onCancel();
    };

    return (
        <Observer>
            {() => (
                <div className={classes.container}>
                    <div className={classes.info_header}>
                        <div className={classes.info_icon_wrapper}>
                            <span className={classes.info_icon}>✓</span>
                        </div>
                        <div className={classes.info_content}>
                            <div className={classes.info_title}>
                                Precio de venta actualizado
                            </div>
                            <div className={classes.info_description}>
                                El precio base fue modificado con una diferencia de{' '}
                                <span className={clsx(
                                    classes.info_difference,
                                    newValueDifference != null && newValueDifference < 0 && classes.info_difference_negative
                                )}>
                                    {formatDifference(newValueDifference)}
                                </span>
                                . <div>¿Deseas propagar este cambio a las agencias que ya tienen precios personalizados?</div>
                            </div>
                        </div>
                    </div>

                    <BoxCard title="" loading={store.loading}>
                        <div className={classes.list_header}>
                            <div className={classes.list_title}>Agencias afectadas</div>
                            <div className={classes.count_badge}>
                                {store.getTotalSelected()} de {store.getTotalAffected()} seleccionadas
                            </div>
                        </div>

                        {store.loading ? (
                            <PageLoading isLoading={true} text="Cargando precios afectados..." />
                        ) : store.error ? (
                            <div className={classes.empty_state}>
                                <div className={classes.empty_state_icon}>⚠️</div>
                                <div className={classes.empty_state_text}>{store.error}</div>
                            </div>
                        ) : store.affectedPrices.length === 0 ? (
                            <div className={classes.empty_state}>
                                <div className={classes.empty_state_icon}>ℹ️</div>
                                <div className={classes.empty_state_text}>
                                    No hay precios afectados para actualizar
                                </div>
                            </div>
                        ) : (
                            <>
                                <div className={classes.prices_list}>
                                    {store.affectedPrices.map((price) => (
                                        <div
                                            key={price.agencyId}
                                            className={clsx(
                                                classes.price_row,
                                                !price.isSelected && classes.price_row_disabled
                                            )}
                                        >
                                            <div className={classes.agency_info}>
                                                <div className={classes.agency_name}>
                                                    {price.agencyName}
                                                </div>
                                                {price.isPersonalized && (
                                                    <div className={classes.agency_label}>
                                                        personalizado
                                                    </div>
                                                )}
                                            </div>

                                            <div className={classes.price_change}>
                                                <span className={classes.old_price}>
                                                    {price.currentPrice}
                                                </span>
                                                <span className={classes.arrow}>→</span>
                                                <span className={classes.new_price}>
                                                    {price.newPrice}
                                                </span>
                                            </div>

                                            <div className={classes.checkbox_container}>
                                                <Checkbox
                                                    text=''
                                                    selected={price.isSelected}
                                                    onChange={() => handleCheckboxChange(price.agencyId)}
                                                />
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                <div className={classes.footer}>
                                    <span className={classes.footer_note}>
                                        Las agencias no seleccionadas mantendrán su precio actual !!!
                                    </span>
                                    <span className={classes.footer_count}>
                                        {store.getTotalSelected()} / {store.getTotalAffected()}
                                    </span>
                                </div>
                            </>
                        )}
                    </BoxCard>
                    <div className={classes.btn_container}>
                        <Button
                            loading={store.loading} className={classes.input} onClick={onSalePriceValuesSave}
                            type={ButtonType.SECONDARY}
                            text={i18n.translate("SAVE")}/>
                        <Button
                            loading={store.loading} className={classes.input} onClick={onHandleCancel}
                            type={ButtonType.DEFAULT}
                            text={i18n.translate("CANCEL")}/>
                    </div>
                </div>
            )}
        </Observer>
    );
};

export default UpdateChildrenArticlePrice;